import { Button, Form, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import AsyncSelect from "react-select/async";
import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { getProductList } from "../Controller/ProductController";
import { debounce } from "lodash";

const VerifyProductForm = ({ 
  onSubmit, 
  onCancel, 
  initialQuantity, 
  productData, 
  loading = false 
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      quantity: initialQuantity ? initialQuantity.toString() : "0.01",
      product: null,
    },
  });

  const product = watch('product');
  const inputValueRef = useRef('');
  const [pendingNewProduct, setPendingNewProduct] = useState(false);
  const submitTimeoutRef = useRef(null);

  // Fetch products based on search input
  const fetchProductOptions = useCallback(async (searchTerm) => {
    if (!searchTerm?.trim()) {
      return [];
    }

    try {
      const response = await getProductList({
        limit: 10,
        offset: 0,
        search: searchTerm,
        store_id: localStorage.getItem('store_id'),
      });

      if (response.success && response.data) {
        return response.data.map((product) => ({
          value: product.slack,
          label: product.name,
          unitPrice: product.purchase_amount_excluding_tax,
          tax: product?.TaxCode?.total_tax_percentage,
        }));
      }
      return [];
    } catch (e) {
      console.error('Error loading products:', e);
      return [];
    }
  }, []);

  // Debounced search with 400ms delay
  const debouncedFetch = useMemo(
    () => debounce((inputValue, callback) => {
      fetchProductOptions(inputValue).then(callback);
    }, 400),
    [fetchProductOptions]
  );

  // Determine if we should show "Save as New" option
  const showSaveAsNew = !product && inputValueRef.current?.trim().length > 0;

  const handleFormSubmit = (data) => {
    // If new product is created, set product.value to empty string
    const formattedData = {
      quantity: parseFloat(data.quantity),
      product: {
        ...data.product,
        value: data.product?.isNew ? "" : data.product?.value,
      },
      saveAsNew: data.product?.isNew || false,
    };

    if (formattedData.saveAsNew) {
      setPendingNewProduct(true);
      submitTimeoutRef.current = setTimeout(() => {
        onSubmit?.(formattedData);
        setPendingNewProduct(false);
      }, 5000);
    } else {
      onSubmit?.(formattedData);
    }
  };

  const validateProduct = (value) => {
    // If saving as new with input value, it's valid
    if (showSaveAsNew) {
      return true;
    }
    // Otherwise, must have a selected product
    return value?.value ? true : "Please select a product";
  };

  const handleProductChange = (option, field) => {
    if (option) {
      field.onChange(option);
      inputValueRef.current = option.label;
    } else {
      field.onChange(null);
      inputValueRef.current = '';
    }
  };

  const handleInputChange = (newValue) => {
    inputValueRef.current = newValue;
  };

  // Clear pending timeout if the form unmounts
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const handleMenuClose = (field) => {
    // If user typed something but didn't select from results, auto-create product
    if (!product && inputValueRef.current?.trim()) {
      const newProduct = {
        value: inputValueRef.current.trim(),
        label: inputValueRef.current.trim(),
        isNew: true,
      };
      field.onChange(newProduct);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Product Search */}
      <Form.Group controlId="product" className="mb-3">
        <Form.Label>
          Merge{' '}
          <span style={{ fontStyle: 'italic', color: 'rgb(40, 167, 69)' }}>
            {productData?.product_name}
          </span>
          {' '}with existing product in store inventory
        </Form.Label>

        <Controller
          control={control}
          name="product"
          rules={{ validate: validateProduct }}
          render={({ field }) => (
            <AsyncSelect
              {...field}
              placeholder="Type to search for an existing product..."
              cacheOptions={false}
              defaultOptions
              loadOptions={debouncedFetch}
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.product ? '#dc3545' : base.borderColor,
                }),
              }}
              onChange={(option) => handleProductChange(option, field)}
              onInputChange={handleInputChange}
              onMenuClose={() => handleMenuClose(field)}
              onBlur={() => {
                // Ensure product is created even if menu didn't close
                if (!field.value && inputValueRef.current?.trim()) {
                  const newProduct = {
                    value: inputValueRef.current.trim(),
                    label: inputValueRef.current.trim(),
                    isNew: true,
                  };
                  field.onChange(newProduct);
                }
                field.onBlur();
              }}
            />
          )}
        />

        {errors.product && !showSaveAsNew && (
          <div className="invalid-feedback d-block">
            {errors.product.message}
          </div>
        )}

        {showSaveAsNew && (
          <div className="alert alert-danger border-danger mt-3 mb-0">
            <div className="fw-semibold">No product found</div>
            <small className="d-block mt-1">
              A new product <strong>"{inputValueRef.current}"</strong> will be created in store inventory.
            </small>
          </div>
        )}
      </Form.Group>

      {/* Quantity Input */}
      <Form.Group controlId="quantity" className="mb-3">
        <Form.Label>Accepted Quantity</Form.Label>
        <Controller
          control={control}
          name="quantity"
          rules={{
            required: 'Quantity is required',
            validate: (v) => {
              const n = parseFloat(v);
              return Number.isFinite(n) && n > 0 
                ? true 
                : 'Enter a number > 0';
            },
          }}
          render={({ field }) => (
            <Form.Control
              {...field}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              placeholder="Enter accepted quantity"
              isInvalid={!!errors.quantity}
            />
          )}
        />
        {errors.quantity && (
          <div className="invalid-feedback d-block">
            {errors.quantity.message}
          </div>
        )}
      </Form.Group>

      <p className="alert alert-warning py-2 px-2 mb-3 small">
        The accepted quantity will be added to the selected product and the same
        quantity will be reduced from the source store inventory.
      </p>

      {pendingNewProduct && (
        <div className="alert alert-info mb-3">
          <div className="fw-semibold">Creating new product</div>
          <small className="d-block mt-1">
            No product found. A new product will be created in store inventory.
          </small>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="outline-secondary"
          onClick={() => {
            if (submitTimeoutRef.current) {
              clearTimeout(submitTimeoutRef.current);
              submitTimeoutRef.current = null;
            }
            setPendingNewProduct(false);
            onCancel?.();
          }}
          disabled={loading || pendingNewProduct}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={loading || pendingNewProduct}>
          {loading || pendingNewProduct ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default VerifyProductForm;